import React, {useState} from 'react';
import {Button, Modal, Table, Tag} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {useEventTable} from "../useEventTable";
import {useEventSegments} from "./useEventSegment";
import {EventSegmentEntity} from "./EventSegmentEntity";
import EventSegmentEditModal from "./EventSegmentEditModal";
import SegmentRegisterModal from "./SegmentRegisterModal";

const rangeTag = (min, max, color, prefix = '') => (
    max === 0 ? <Tag>제한 없음</Tag> : <Tag color={color}>{prefix}{min} ~ {prefix}{max}</Tag>
);

// 개인화 테이블: 세그먼트 그룹(event_segment + event_segment_group). [추가하기] 로 그룹의 이벤트 전체를 개인화 관리에 편성한다.
const EventSegment = () => {
    const [editRecord, setEditRecord] = useState(null);
    const [registerRecord, setRegisterRecord] = useState(null);

    const {data: eventTable, isLoading, error} = useEventTable();
    const {data: segmentList, isLoading: isSegmentLoading, error: segmentError, refetch} = useEventSegments();

    const handleCloseEdit = () => {
        setEditRecord(null);
        refetch();
    };

    const itemColumns = [
        {
            title: '이벤트', dataIndex: 'typeIndex', key: 'typeIndex',
            render: (typeIndex) => `${eventTable?.[typeIndex]?.nameText || ''}(${typeIndex})`,
        },
        {
            title: '평균 결제 금액(달러)', key: 'avrPayment', width: 180,
            render: (_, r) => rangeTag(Number(r.minAvrPayment), Number(r.maxAvrPayment), 'blue', '$'),
        },
        {title: '레벨', key: 'level', width: 140, render: (_, r) => rangeTag(r.minLevel, r.maxLevel, 'green')},
    ];

    const columns = [
        {title: '관리 번호', dataIndex: 'seq', key: 'seq', width: 90, sorter: (a, b) => a.seq - b.seq},
        {title: '메모', dataIndex: 'memo', key: 'memo'},
        {title: '등록된 이벤트 수', key: 'itemCount', width: 130, render: (_, r) => `${r.items.length}개`},
        {title: '마지막 결제일', dataIndex: 'lastPayment', key: 'lastPayment', width: 110},
        {title: '평균 결제일', dataIndex: 'averagePayment', key: 'averagePayment', width: 100},
        {title: '결제 횟수(이상)', dataIndex: 'paymentCount', key: 'paymentCount', width: 120},
        {
            title: '누적 기준일', dataIndex: 'baseDate', key: 'baseDate', width: 110,
            render: (v) => v ? dayjs(v).format('YYYY.MM.DD') : '-',
        },
        {title: '누적 로그인 일수(이상)', dataIndex: 'cumulativeLoginCount', key: 'cumulativeLoginCount', width: 160},
        {
            title: '누적 결제 금액(달러)', key: 'cumulativePayment', width: 150,
            render: (_, r) => <Tag color={r.isCumPaymentOver ? 'blue' : 'orange'}>{r.cumulativePayment} {r.isCumPaymentOver ? '▲' : '▼'}</Tag>,
        },
        {
            title: '액션', key: 'action', width: 100,
            render: (_, record) => (
                <Button size="small" type="primary"
                        disabled={record.items.length === 0}
                        onClick={e => {
                            e.stopPropagation();
                            setRegisterRecord(record);
                        }}>
                    추가하기
                </Button>
            ),
        },
    ];

    if (isLoading || isSegmentLoading) return <IsLoading/>;
    if (error || segmentError) return <ErrorPage error={error || segmentError}/>;

    return (
        <div>
            <Button type="primary" icon={<PlusOutlined/>} style={{marginBottom: 16}}
                    onClick={() => setEditRecord(new EventSegmentEntity())}>
                신규 추가
            </Button>

            <Table
                columns={columns}
                dataSource={(segmentList || []).map(row => ({...row, key: row.seq}))}
                expandable={{
                    expandedRowRender: (record) => (
                        <Table columns={itemColumns} dataSource={record.items.map(item => ({...item, key: item.seq}))}
                               pagination={false} size="small"/>
                    ),
                }}
                onRow={(record) => ({onClick: () => setEditRecord(record)})}
                rowClassName={() => 'cursor-pointer'}
                size="middle"
                scroll={{x: 'max-content'}}
            />

            <Modal
                open={!!editRecord}
                onCancel={() => setEditRecord(null)}
                footer={null}
                width={1000}
                title={editRecord?.seq ? `세그먼트 그룹 편집 (관리 번호: ${editRecord.seq})` : '신규 세그먼트 그룹'}
                destroyOnClose
            >
                {editRecord && <EventSegmentEditModal record={editRecord} eventTable={eventTable} handleClose={handleCloseEdit}/>}
            </Modal>

            <Modal
                open={!!registerRecord}
                onCancel={() => setRegisterRecord(null)}
                footer={null}
                width={800}
                title={`세그먼트 이벤트 등록 (관리 번호: ${registerRecord?.seq})`}
                destroyOnClose
            >
                {registerRecord && <SegmentRegisterModal segment={registerRecord} eventTable={eventTable} handleClose={() => setRegisterRecord(null)}/>}
            </Modal>
        </div>
    );
};

export default EventSegment;
